import { useEffect, useRef } from 'react';
import type { FunctionComponent } from 'react';
import useIsVisibleOnScreen from '../../../hooks/useIsVisibleOnScreen';

type Props = {
  // Function to update the parent component when the element is visible on screen
  onElementVisible: (isVisible: boolean) => void,
  // Css class(s)
  className?: string,
};

/**
 * A wrapper component that will notify the parent component when it is visible on screen.
 * @example
 * const [isVisible, setIsVisible] = useState(false);
 * return (
 *   <>
 *     <VisibilityWrapper element={element} onElementVisible={setIsVisible} />
 *     {isVisible && <div>Visible!</div>}
 *   </>
 * );
 */
const VisibilityWrapper: FunctionComponent<Props> = ({
  onElementVisible,
  className,
  children,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const isVisible = useIsVisibleOnScreen(ref);

  // Update the parent component when the element is visible on screen.
  useEffect(() => {
    onElementVisible(isVisible);
  }, [isVisible, onElementVisible]);

  return (
    <div ref={ref} className={className}>
      {children}
    </div>
  );
};

export default VisibilityWrapper;
